import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CodeMirror from '@uiw/react-codemirror';
import { markdown } from '@codemirror/lang-markdown';
import {
  createPersona,
  fetchSupportedLocalePairs,
  localeToIso,
  formatLocale,
} from './api';
import './ChatList.css';

// Maps the active app theme to the editor's colour mode. Any dark-spirited theme
// (its name ends in 'dark', e.g. 'dark' or 'nature-dark') uses the dark editor;
// everything else uses the light editor.
function editorModeFor(theme) {
  return (theme || '').endsWith('dark') ? 'dark' : 'light';
}

// Tracks the active editor colour mode ('light' | 'dark') by observing the
// data-theme attribute on <html> (see theme.js), so the CodeMirror editor
// follows the theme selector live.
function useThemeMode() {
  const [mode, setMode] = useState(() =>
    editorModeFor(document.documentElement.dataset.theme)
  );

  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      setMode(editorModeFor(root.dataset.theme));
    });
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  return mode;
}

// Form to create a persona owned by the current user. Mirrors
// dev.c15u.autolang.web.api.dto.persona.CreatePersonaRequest.
//
// The persona's originalLocale is the language being practised, so we only offer
// the persona locales the backend can actually teach (the same set used to filter
// the personae in CreateChatForm), fetched from /api/v0/locale.
function CreatePersonaForm({ onCreated, onCancel }) {
  const { t } = useTranslation();
  // Read the latest translator inside the locale-loading effect without making it
  // a dependency: re-running that effect on a UI-language switch would refetch and
  // reset the user's chosen persona language.
  const tRef = useRef(t);
  tRef.current = t;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [script, setScript] = useState('');
  const [localePairs, setLocalePairs] = useState([]);
  const [originalLocaleIso, setOriginalLocaleIso] = useState('');
  // '' is the uncast state, not a fifth voice: nobody has chosen what this persona sounds like.
  const [voice, setVoice] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const themeMode = useThemeMode();

  // Load the persona locales the backend supports, de-duplicated by ISO code.
  useEffect(() => {
    let cancelled = false;

    const loadLocales = async () => {
      try {
        const pairs = await fetchSupportedLocalePairs();
        if (cancelled) return;

        const seen = new Set();
        const locales = [];
        for (const pair of pairs) {
          const iso = localeToIso(pair.personaLocale);
          if (!seen.has(iso)) {
            seen.add(iso);
            locales.push(pair.personaLocale);
          }
        }

        setLocalePairs(locales);
        if (locales.length > 0) {
          setOriginalLocaleIso(localeToIso(locales[0]));
        }
      } catch (err) {
        if (!cancelled) setError(tRef.current('persona.loadLanguagesError'));
      }
    };

    loadLocales();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // CodeMirror is not a native form control, so the browser's `required`
    // validation does not cover the script; check it here.
    if (!script.trim()) {
      setError(t('persona.describeScript'));
      return;
    }

    const originalLocale = localePairs.find(
      (locale) => localeToIso(locale) === originalLocaleIso
    );
    if (!originalLocale) {
      setError(t('persona.chooseLanguage'));
      return;
    }

    setIsSubmitting(true);

    try {
      const persona = await createPersona({
        name,
        description,
        script,
        originalLocale: {
          languageCode: originalLocale.languageCode,
          countryCode: originalLocale.countryCode,
        },
        voice: voice || null,
      });
      onCreated(persona);
    } catch (err) {
      setError(err.message || t('common.networkError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="create-chat-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="persona-name">{t('persona.name')}</label>
        <input
          type="text"
          id="persona-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          disabled={isSubmitting}
        />
      </div>
      <div className="form-group">
        <label htmlFor="persona-description">{t('persona.description')}</label>
        <textarea
          id="persona-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          disabled={isSubmitting}
          rows={2}
        />
      </div>
      <div className="form-group">
        {/* CodeMirror renders its own editable surface (not a real form control),
            so the label is associated by proximity rather than htmlFor. */}
        <label>
          {t('persona.scriptMarkdown')}
          <button
            type="button"
            onClick={() => navigator.clipboard.writeText(`
**Act as "Elias", a warm and professional clerk at 'The Golden Crust,' a cozy artisanal bakery from England.
Your goal is to assist customers with their purchases while staying strictly in character.**

### Your Personality & Tone:

* **Welcoming:** You greet every customer as if they’ve just walked out of the cold into a warm kitchen.
* **Knowledgeable:** You know your ingredients (organic flour, sourdough starters, local honey).
* **Slightly Rustic:** Use descriptive, sensory language (e.g., "crusty," "flaky," "oven-warm," "aromatic").
* **Brief:** You answer in one to three sentences.

### Strict Constraints:

1. **Never Break Character:** Do not acknowledge that you are an AI. Do not discuss programming, LLMs, or the outside
   world beyond the bakery's neighborhood.
2. **Product Knowledge:** If a customer asks for something a bakery wouldn't have (like a steak or a smartphone),
   politely explain that you only specialize in baked goods and perhaps suggest a nearby fictional shop.
3. **No Modern Tech Talk:** Refer to payments as "at the register" or "taking your card/cash." Do not discuss digital
   processing or APIs.
4. **The "Oven" Rule:** If you need a moment to "think" or process a large request, frame it as "letting the next batch
   finish browning."

### Opening Line:

Propose something random from the inventory.

### Wrap up

Never forget to ask for payment. After the customer paid, wish them a good day.
            `)}
          >
            {t('persona.copyExample')}
          </button>
        </label>
        <CodeMirror
          className="persona-script-editor"
          value={script}
          onChange={(value) => setScript(value)}
          extensions={[markdown()]}
          theme={themeMode === 'dark' ? 'dark' : 'light'}
          editable={!isSubmitting}
          minHeight="320px"
          basicSetup={{
            lineNumbers: false,
            foldGutter: false,
            highlightActiveLineGutter: false,
          }}
        />
      </div>
      <div className="form-group">
        <label htmlFor="persona-locale">{t('persona.personaLanguage')}</label>
        <select
          id="persona-locale"
          value={originalLocaleIso}
          onChange={(e) => setOriginalLocaleIso(e.target.value)}
          required
          disabled={isSubmitting || localePairs.length === 0}
        >
          {localePairs.length === 0 && <option value="">{t('persona.noLanguages')}</option>}
          {localePairs.map((locale) => {
            const iso = localeToIso(locale);
            return (
              <option key={iso} value={iso}>
                {formatLocale(locale)}
              </option>
            );
          })}
        </select>
      </div>
      <div className="form-group">
        <label htmlFor="persona-voice">{t('persona.voice')}</label>
        <select
          id="persona-voice"
          value={voice}
          onChange={(e) => setVoice(e.target.value)}
          disabled={isSubmitting}
        >
          <option value="">{t('persona.voiceUnset')}</option>
          <option value="YOUNG_FEMININE">{t('persona.voiceYoungFeminine')}</option>
          <option value="ADULT_FEMININE">{t('persona.voiceAdultFeminine')}</option>
          <option value="YOUNG_MASCULINE">{t('persona.voiceYoungMasculine')}</option>
          <option value="ADULT_MASCULINE">{t('persona.voiceAdultMasculine')}</option>
        </select>
      </div>
      {error && <div className="error-message">{error}</div>}
      <div className="create-chat-actions">
        <button type="button" className="cancel-button" onClick={onCancel} disabled={isSubmitting}>
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          className="create-button"
          disabled={isSubmitting || !name || !originalLocaleIso}
        >
          {isSubmitting ? t('persona.creating') : t('persona.create')}
        </button>
      </div>
    </form>
  );
}

// Full page hosting the persona creation form, reached from the "New persona"
// button on the chat list. Returns to the chat list on success or cancel.
function NewPersonaPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = () => navigate('/chats');

  return (
    <div className="chat-list-container form-page-container">
      <div className="chat-list-header form-page-header">
        <h2>{t('persona.pageTitle')}</h2>
      </div>
      <div className="chat-list-content">
        <CreatePersonaForm onCreated={goBack} onCancel={goBack} />
      </div>
    </div>
  );
}

export { CreatePersonaForm };
export default NewPersonaPage;
